import React, { useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@utils/stores/authStore.ts";
import { Center, Loader } from "@mantine/core";
import { AppRoutes } from "@/constants.ts";
import { useFeatureFlagStore } from "@/store/featureFlagStore";
import { isContractUpgradeSnoozeActive } from "@/utils/contractUpgradeSnoozeCookie";
import { loginPathWithRedirect } from "@/pages/LoginManagement/utils/redirect";
import { isContractSubscriptionMismatch } from "@smartwiz/shared-types";

/**
 * True when an admin impersonation session cookie is present (skip upgrade prompt).
 */
function isAdminImpersonationSession(): boolean {
  try {
    const cookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("admin_impersonation_info="));
    if (!cookie) return false;
    const value = decodeURIComponent(cookie.split("=")[1] ?? "");
    const info = JSON.parse(value) as { is_impersonation?: boolean };
    return info.is_impersonation === true;
  } catch {
    return false;
  }
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string | string[];
  requiredBusinessId?: string | string[];
  /** When true, only users with a service bureau profile may access the route. */
  requiresServiceBureau?: boolean;
}

const matchesRequirement = (
  userValue: string | undefined,
  required?: string | string[]
) => {
  if (!required) return true;
  if (!userValue) return false;
  if (Array.isArray(required)) {
    return required.includes(userValue);
  }
  return userValue === required;
};

const ProtectedRoute = ({
  children,
  requiredRole,
  requiredBusinessId,
  requiresServiceBureau,
}: ProtectedRouteProps) => {
  const { user, loading, billingContext } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const initializeFlags = useFeatureFlagStore((s) => s.initialize);
  const flagsInitialized = useFeatureFlagStore((s) => s.isInitialized);
  const flagsLoading = useFeatureFlagStore((s) => s.loading);
  const hasContractUpgradeFlag = useFeatureFlagStore((s) =>
    s.hasFlag("CONTRACT_UPGRADE_PROMPT")
  );

  useEffect(() => {
    if (user?.role === "owner") {
      void initializeFlags();
    }
  }, [user?.role, initializeFlags]);

  const unauthorized =
    !loading &&
    user?.email &&
    (!matchesRequirement(user.role ?? undefined, requiredRole) ||
      !matchesRequirement(user.businessId ?? undefined, requiredBusinessId));

  // Check if subscription is inactive (not active AND not in trial)
  // Applies to ALL roles including clients (clients rely on their tax pro's subscription)
  const subscriptionInactive =
    !loading && user?.email && !user.isActive && !user.isTrialActive;

  // Determine redirect destination based on role and subscription history
  const getInactiveRedirect = () => {
    // Managed business owners resume onboarding from backend-derived billing state.
    if (
      user?.role === "owner" &&
      billingContext?.mode === "managed_by_service_bureau"
    ) {
      return AppRoutes.gettingStarted;
    }
    // Clients always go to expired subscription page (they can't reactivate themselves)
    if (user?.role === "client") {
      return AppRoutes.subscriptionExpired;
    }
    // Team members and team leads also go to expired subscription page
    if (user?.role === "team_member" || user?.role === "team_lead") {
      return AppRoutes.subscriptionExpired;
    }
    // Owners: check if they had a subscription before
    if (user?.hadSubscription) {
      // User had a subscription before - show expired subscription page
      return AppRoutes.subscriptionExpired;
    }
    // Owner never had a subscription - show onboarding flow
    return AppRoutes.gettingStarted;
  };

  useEffect(() => {
    if (unauthorized) {
      if (window.history.length > 2) {
        navigate(-1);
      } else {
        navigate(AppRoutes.root, { replace: true });
      }
    }
  }, [unauthorized, navigate]);

  if (loading)
    return (
      <Center w="100vw" h="100vh">
        <Loader color="blue" />
      </Center>
    );
  if (!user?.email) {
    return (
      <Navigate
        to={loginPathWithRedirect(
          `${location.pathname}${location.search}${location.hash}`
        )}
        replace
      />
    );
  }

  // Redirect to payment update page if payment method needs updating
  if (user?.requiresPaymentUpdate) {
    return <Navigate to={AppRoutes.paymentUpdate} replace />;
  }

  // Contract/subscription mismatch — support-only page (never onboarding/reactivate)
  if (isContractSubscriptionMismatch(billingContext)) {
    return <Navigate to={AppRoutes.billingAccountIssue} replace />;
  }

  // Redirect based on subscription status
  if (subscriptionInactive) {
    return <Navigate to={getInactiveRedirect()} replace />;
  }

  if (unauthorized) return null;

  if (requiresServiceBureau && user.isServiceBureau !== true) {
    const fallbackHome = user.useLiteHome ? AppRoutes.homeLite : AppRoutes.home;
    return <Navigate to={fallbackHome} replace />;
  }

  // --- Temporary contract upgrade campaign (owners): redirect to /contract-upgrade ---
  const path = location.pathname;
  const onContractUpgradeRoute =
    path === AppRoutes.contractUpgrade ||
    path.startsWith(`${AppRoutes.contractUpgrade}/`);
  // Admin debug tools live under /admin/... (avoid matching /admin-login, etc.).
  const onAdminToolPath = path === "/admin" || path.startsWith("/admin/");
  if (onContractUpgradeRoute || onAdminToolPath) {
    return <>{children}</>;
  }
  if (
    user.role === "owner" &&
    !isAdminImpersonationSession() &&
    user.needsContractUpgrade === true &&
    flagsInitialized &&
    !flagsLoading &&
    hasContractUpgradeFlag &&
    !isContractUpgradeSnoozeActive()
  ) {
    return <Navigate to={AppRoutes.contractUpgrade} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
